import Immutable from 'seamless-immutable';
import PropTypes from 'prop-types';
import { retrieveErrors } from '@widergy/web-utils/lib/message';
import { isEmpty } from '@widergy/web-utils/lib/array';
import { AlertHandler } from '@widergy/energy-ui';

import * as NotificationService from '../services/NotificationService';
import { message } from '../utils/stringUtils';

import { actionCreators as accountActions } from './accountHandlers';
import { actionCreators as authActions } from './authHandlers';
import { stringArrayToObject } from './utils';

/* ------------- Notification actions ------------- */
export const actions = stringArrayToObject(
  [
    'GET_ACCOUNT_NOTIFICATIONS',
    'GET_ACCOUNT_NOTIFICATIONS_SUCCESS',
    'GET_ACCOUNT_NOTIFICATIONS_FAILURE',
    'SET_NOTIFICATIONS_SEEN',
    'SET_NOTIFICATIONS_SEEN_SUCCESS',
    'SET_NOTIFICATIONS_SEEN_FAILURE',
    'SET_NOTIFICATION_AS_READ',
    'SET_NOTIFICATION_AS_READ_SUCCESS',
    'SET_NOTIFICATION_AS_READ_FAILURE',
    'APPEND_NOTIFICATIONS',
    'APPEND_NOTIFICATIONS_SUCCESS',
    'APPEND_NOTIFICATIONS_FAILURE',
    'APPEND_NEW_NOTIFICATIONS_SUCCESS'
  ],
  '@@NOTIFICATION'
);

const privateActionCreators = {
  getAccountNotificationsSuccess: notifications => ({
    type: actions.GET_ACCOUNT_NOTIFICATIONS_SUCCESS,
    payload: notifications,
    target: 'notificationList'
  }),
  appendNotificationsSuccess: notifications => ({
    type: actions.APPEND_NOTIFICATIONS_SUCCESS,
    payload: notifications,
    target: 'notificationList'
  }),
  getAccountNotificationsFailure: error => ({
    type: actions.GET_ACCOUNT_NOTIFICATIONS_FAILURE,
    payload: { error },
    target: 'notificationList'
  }),
  appendNotificationsFailure: error => ({
    type: actions.APPEND_NOTIFICATIONS_FAILURE,
    payload: { error },
    target: 'notificationList'
  }),
  setNotificationsSeenSuccess: () => ({
    type: actions.SET_NOTIFICATIONS_SEEN_SUCCESS,
    target: 'notificationList'
  }),
  setNotificationsSeenFailure: error => ({
    type: actions.SET_NOTIFICATIONS_SEEN_FAILURE,
    payload: { error },
    target: 'notificationList'
  }),
  setNotificationAsReadSuccess: notification => ({
    type: actions.SET_NOTIFICATION_AS_READ_SUCCESS,
    target: 'notificationList',
    payload: notification
  }),
  setNotificationAsReadFailure: error => ({
    type: actions.SET_NOTIFICATION_AS_READ_FAILURE,
    payload: { error },
    target: 'notificationList'
  })
};

export const actionCreators = {
  getUserNotifications: (pageSize = 5) => async (dispatch, getState) => {
    const { currentAccount } = getState().account;
    if (!currentAccount) return;
    dispatch({ type: actions.GET_ACCOUNT_NOTIFICATIONS, target: 'notificationList' });
    const response = await NotificationService.getUserNotifications(currentAccount.id, 1, pageSize);
    if (response.ok) {
      dispatch(privateActionCreators.getAccountNotificationsSuccess(response.data));
      if (isEmpty(response.data)) AlertHandler.info(message('Notification:withoutNotifications'));
    } else {
      AlertHandler.error(
        retrieveErrors(response.data, message('DefaultMessages:getUserNotificationsFailure'))
      );
      dispatch(privateActionCreators.getAccountNotificationsFailure(response.problem));
    }
  },
  appendNotifications(pageNumber, pageSize) {
    return async (dispatch, getState) => {
      const { currentAccount } = getState().account;

      if (!currentAccount) return;

      const accountId = currentAccount.id;
      dispatch({ type: actions.APPEND_NOTIFICATIONS, target: 'notificationList' });
      const response = await NotificationService.getUserNotifications(accountId, pageNumber, pageSize);
      if (response.ok) {
        dispatch(privateActionCreators.appendNotificationsSuccess(response.data, pageNumber));
      } else {
        AlertHandler.error(
          retrieveErrors(response.data, message('DefaultMessages:appendNotificationsFailure'))
        );
        dispatch(privateActionCreators.appendNotificationsFailure(response.problem));
      }
    };
  },
  setNotificationsSeen() {
    return async (dispatch, getState) => {
      const { currentAccount } = getState().account;
      if (!currentAccount) return;
      dispatch({ type: actions.SET_NOTIFICATIONS_SEEN, target: 'notificationList' });
      const response = await NotificationService.setNotificationsSeen(currentAccount.id);
      if (response.ok) {
        dispatch(privateActionCreators.setNotificationsSeenSuccess());
        dispatch(accountActions.cleanAccountNewNotifications());
        dispatch(authActions.cleanUserNewNotifications());
      } else {
        AlertHandler.error(
          retrieveErrors(response.data, message('DefaultMessages:setNotificationsSeenFailure'))
        );
        dispatch(privateActionCreators.setNotificationsSeenFailure(response.problem));
      }
    };
  },
  setNotificationAsRead(notificationId) {
    return async dispatch => {
      dispatch({ type: actions.SET_NOTIFICATION_AS_READ, target: 'notificationList' });
      const response = await NotificationService.setNotificationAsRead(notificationId);
      if (response.ok) {
        dispatch(privateActionCreators.setNotificationAsReadSuccess(response.data));
      } else {
        AlertHandler.error(
          retrieveErrors(response.data, message('DefaultMessages:setNotificationAsReadFailure'))
        );
        dispatch(privateActionCreators.setNotificationAsReadFailure(response.problem));
      }
    };
  }
};

/* ------------- Notification reducer ------------- */
const defaultState = {
  notificationList: [],
  notificationListLoading: false,
  notificationListError: null
};

/* ------------- Notification reducer ------------- */
/* eslint-disable complexity */
export function reducer(state = Immutable(defaultState), action) {
  switch (action.type) {
    case actions.GET_ACCOUNT_NOTIFICATIONS: {
      return Immutable.merge(state, {
        [action.target]: []
      });
    }
    case actions.APPEND_NOTIFICATIONS:
    case actions.SET_NOTIFICATIONS_SEEN:
    case actions.SET_NOTIFICATION_AS_READ: {
      return Immutable.merge(state, {
        [`${action.target}Loading`]: true
      });
    }
    case actions.GET_ACCOUNT_NOTIFICATIONS_FAILURE:
    case actions.APPEND_NOTIFICATIONS_FAILURE:
    case actions.SET_NOTIFICATIONS_SEEN_FAILURE:
    case actions.SET_NOTIFICATION_AS_READ_FAILURE: {
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        [`${action.target}Error`]: { what: action.payload.error, where: action.type }
      });
    }
    case actions.SET_NOTIFICATIONS_SEEN_SUCCESS: {
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false
      });
    }
    case actions.SET_NOTIFICATION_AS_READ_SUCCESS: {
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        [action.target]: state[action.target].map(notification =>
          notification.id === action.payload.id
            ? { ...notification, status: action.payload.status }
            : notification
        ),
        [`${action.target}Error`]: null
      });
    }
    case actions.GET_ACCOUNT_NOTIFICATIONS_SUCCESS: {
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        [action.target]: action.payload
      });
    }
    case actions.APPEND_NOTIFICATIONS_SUCCESS: {
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        [action.target]: [...state.notificationList, ...action.payload]
      });
    }
    default: {
      return state;
    }
  }
}
/* eslint-enable complexity */

/* ------------- Notification propTypes ------------- */
export const notificationPropTypes = PropTypes.shape({
  id: PropTypes.number.isRequired,
  body: PropTypes.string.isRequired,
  created_at: PropTypes.string.isRequired,
  entity_id: PropTypes.number,
  form_type: PropTypes.string,
  notification_type: PropTypes.string.isRequired,
  read: PropTypes.bool.isRequired
});
